import { AxiosResponse } from "axios";
import { TestRequestAdvancedSearchRQ } from "../models/advanced-search/TestRequestAdvancedSearchRQ";
import ApiResponse from "../models/ApiResponse";
import PagedResponse from "../models/PagedResponse";
import { TestRequestSearchResult } from "../models/TestRequest";
import Api from "./Api";
import WaitingFilter from "../models/WaitingFilter";
import { TestRequestCountResponse } from "./TestRequestApi";
import downloadFile from "../../functions/downloadFile.function";
import { SortDirection } from "../../helper/UserSettingsHelper";
import { TestRequestSortColumn } from "../../pages/Dashboard/TestRequestsTable/TestRequestsOverviewColumns";
import LabsAndUsersResponse from "../models/advanced-search/LabsAndUsersResponse";
import TestRequestExportSnapshotResponse from "../models/advanced-search/TestRequestExportSnapshotResponse";
import TestRequestExportParameterResponse from "../models/advanced-search/TestRequestExportParameterResponse";

class AdvancedSearchApi {
  constructor(private api: Api) {}

  public searchTestRequests(
    searchParams: {
      labKey?: string;
      pageIndex: number;
      pageSize: number;
      requesterEmail?: string;
      sortColumn: string;
      sortDirection: string;
      statesQueryString: string;
      waitingFilter?: WaitingFilter;
    },
    payload?: TestRequestAdvancedSearchRQ,
    searchArchive?: boolean,
  ): ApiResponse<PagedResponse<TestRequestSearchResult>> {
    const { pageIndex, pageSize, requesterEmail, sortColumn, sortDirection, statesQueryString, waitingFilter, labKey } =
      searchParams;

    let basePath = "/test-request/v2/advanced-search";

    if (!searchArchive) {
      basePath += "/dashboard";
    }
    const waitingFilterPart = waitingFilter ? `&waitingFilter=${waitingFilter}` : "";
    const requesterEmailPart = requesterEmail ? `&requestorEmail=${requesterEmail}` : "";
    const labPart = labKey ? `&labs=${labKey}` : "";

    return this.api.post(
      `${basePath}?pageIndex=${pageIndex || 0}&pageSize=${pageSize}&sortColumn=${
        sortColumn || "CREATION_DATE"
      }&sortDirection=${sortDirection && sortDirection.toUpperCase()}${
        statesQueryString || ""
      }${waitingFilterPart}${requesterEmailPart}${labPart}`,
      payload,
    );
  }

  public countTestRequests(
    requestorEmail: string | undefined,
    labKey: string | undefined,
    waitingFilter?: WaitingFilter,
    payload?: TestRequestAdvancedSearchRQ,
  ): Promise<AxiosResponse<TestRequestCountResponse>> {
    const requestorEmailQueryPart = requestorEmail ? `requestorEmail=${requestorEmail}` : "";
    const labKeyPart = labKey ? `&labs=${labKey}` : "";
    const waitingFilterPart = waitingFilter ? `waitingFilter=${waitingFilter}` : "";

    return this.api.post(
      `/test-request/v2/advanced-search/count?${requestorEmailQueryPart}${labKeyPart}&${waitingFilterPart}`,
      payload,
    );
  }

  public searchLabsAndUsers(searchTerm: string): ApiResponse<LabsAndUsersResponse> {
    return this.api.get(`/test-request/v2/testrequests/labs-and-users?searchTerm=${searchTerm}`);
  }

  public prepareDataSnapshot(
    payload: TestRequestAdvancedSearchRQ,
    sortColumn?: TestRequestSortColumn,
    sortDirection?: SortDirection,
  ): ApiResponse<TestRequestExportSnapshotResponse> {
    return this.api.post(
      `/test-request/v2/advanced-search/export-snapshot?sortColumn=${sortColumn || ""}&sortDirection=${
        sortDirection?.toUpperCase() || ""
      }`,
      payload,
    );
  }

  public exportExcelWithResults(
    snapshotId: string,
    substrateClassIds: number[],
    tesaProducts: string[],
    testMethodIds: number[],
  ) {
    const params = new URLSearchParams();
    substrateClassIds.forEach((id) => params.append("substrateClassIds", `${id}`));
    tesaProducts.forEach((id) => params.append("tesaProducts", `${id}`));
    testMethodIds.forEach((id) => params.append("testMethodIds", `${id}`));
    const query = params.toString() ? `?${params.toString()}` : "";
    return this.api
      .get(`/test-request/v2/advanced-search/export-snapshot/${snapshotId}/extended-results${query}`, {
        responseType: "arraybuffer",
      })
      .then((res) => downloadFile(res, "MarketingExport.xls"));
  }

  public getExportSnapshotParameters(snapshotId: string): ApiResponse<TestRequestExportParameterResponse> {
    return this.api.get(`/test-request/v2/advanced-search/export-snapshot/${snapshotId}/parameters`);
  }

  public exportExcelWithoutResults(snapshotId: string) {
    return this.api
      .get(`/test-request/v2/advanced-search/export-snapshot/${snapshotId}/results`, {
        responseType: "arraybuffer",
      })
      .then((res) => downloadFile(res, "MarketingExport.xls"));
  }
}

export default AdvancedSearchApi;
